import type {ILocationToken} from "./location-selection-types";
import {
    ISemanticGroup,
    isSemanticGroupCountryOnlyMember,
    partitionFullSemanticGroupSelections,
} from "./location-semantic-group-collapse";

/** Represents a group of location tokens under a parent label (country or semantic group). */
export interface ILocationLabelsGroup {
    parentLabel: string;
    tokens: ILocationToken[];
}

/**
 * Result structure for rendering location tags in UI.
 *
 * Semantics:
 * - isSelectAll = true → render "All locations" tag
 * - isSelectAll = false, groups = [] → render nothing
 * - isSelectAll = false, groups = [data] → render parent/member/province/city tags
 */
export interface ILocationLabelsGroups {
    isSelectAll: boolean;
    groups: Array<ILocationLabelsGroup>;
}

/**
 * LocationLabelsGrouper (stateless utility) - groups location tokens for rendering tags.
 * Expects UI tokens (group/country codes); parent labels are resolved to display names by the caller.
 */
class LocationLabelsGrouper {

    constructor(private readonly _semanticGroups: ISemanticGroup[] = []) {}

    /**
     * Groups tokens by parent label. Returns isSelectAll flag and grouped tokens.
     * undefined → no tags, [] → "All locations" tag, [tokens] → grouped tags.
     */
    public getGroupedLabels(tokens: ILocationToken[] | undefined): ILocationLabelsGroups {
        const isSelectAll = tokens?.length === 0;

        if (isSelectAll) {
            return {isSelectAll: true, groups: []};
        }

        if (!tokens?.length) {
            return {isSelectAll: false, groups: []};
        }

        const {fullGroupKeys, remainingTokens} = partitionFullSemanticGroupSelections(
            tokens,
            this._semanticGroups,
            {
                resolveGroupKey: (group) => group.code,
                isCountryOnlyMember: isSemanticGroupCountryOnlyMember,
            }
        );

        /** Fully selected semantic groups become a single parent chip; partial members stay in remainingTokens. */
        const groups: ILocationLabelsGroup[] = fullGroupKeys.map((groupCode) => ({
            parentLabel: this._getGroupLabel(groupCode),
            tokens: [],
        }));

        const groupsMap = new Map<string, ILocationToken[]>();

        /** Partial semantic-group members and standalone countries each get their own parent chip. */
        for (const token of remainingTokens) {
            const tokenCountryCode = token.country;

            if (!tokenCountryCode) {
                continue;
            }

            if (!groupsMap.has(tokenCountryCode)) {
                groupsMap.set(tokenCountryCode, []);
            }

            groupsMap.get(tokenCountryCode)!.push(token);
        }

        groups.push(
            ...Array.from(groupsMap.entries()).map(([tokenCountryCode, groupTokens]) => ({
                parentLabel: tokenCountryCode,
                tokens: groupTokens,
            }))
        );

        return {isSelectAll: false, groups};
    }

    private _getGroupLabel(groupCode: string): string {
        return this._semanticGroups.find((group) => group.code === groupCode)?.label ?? groupCode;
    }
}

export default LocationLabelsGrouper;
